import { useUser } from "@clerk/clerk-react"; // Clerk useUser importieren
import { api } from "@convex/_generated/api"; // Use alias
import type { Doc, Id } from "@convex/_generated/dataModel"; // Use alias
import { useMutation } from "convex/react";
import {
	Archive,
	AudioLines,
	Download,
	FileQuestion,
	FileText,
	Image,
	Trash2,
	Video,
} from "lucide-react";
import type React from "react";
import { Button } from "@/components/ui/button";
import { formatBytes } from "@/lib/utils";

interface FileListProps {
	dateien: Array<
		Doc<"files"> & {
			url: string | null; // Add URL property
		}
	>;
	// nutzerHash: string; // nutzerHash wird nicht mehr benötigt
	istBesitzer: boolean;
	nurBesitzerLoeschen: boolean;
	onSuccess: (message: string) => void;
	onError: (message: string) => void;
}

// Helper to get an icon based on MIME type
const getFileIcon = (mimeType: string): React.ElementType => {
	if (mimeType.startsWith("image/")) return Image;
	if (mimeType.startsWith("video/")) return Video;
	if (mimeType.startsWith("audio/")) return AudioLines;
	if (mimeType === "application/pdf" || mimeType.includes("text"))
		return FileText;
	if (mimeType.includes("zip") || mimeType.includes("archive")) return Archive;
	return FileQuestion; // Default icon
};

export function FileList({
	dateien,
	// nutzerHash, // nutzerHash wird nicht mehr benötigt
	istBesitzer,
	nurBesitzerLoeschen,
	onSuccess,
	onError,
}: FileListProps) {
	const { user } = useUser(); // Aktuellen Clerk-Benutzer laden
	const dateiLoeschenMutation = useMutation(api.fileShare.dateiLoeschen);

	const handleDelete = async (fileId: Id<"files">, fileName: string) => {
		try {
			// await dateiLoeschenMutation({ fileId, nutzerHash }); // nutzerHash aus Aufruf entfernt
			await dateiLoeschenMutation({ fileId }); // Nur fileId übergeben
			onSuccess(`Datei "${fileName}" erfolgreich gelöscht.`);
		} catch (error: unknown) {
			onError(
				`Fehler beim Löschen von "${fileName}": ${
					(error as Error).message || String(error)
				}`,
			);
			console.error("Error deleting file:", error);
		}
	};

	const canDelete = (datei: Doc<"files">): boolean => {
		// Prüfen, ob der aktuelle Benutzer der Uploader der Datei ist
		const istUploader = !!user && datei.clerkUserId === user.id;
		if (nurBesitzerLoeschen) {
			return istBesitzer; // Only owner can delete if nurBesitzerLoeschen is true
		}
		// If !nurBesitzerLoeschen, owner or uploader can delete
		return istBesitzer || istUploader;
	};

	if (dateien.length === 0) {
		return (
			<div className="card rounded-xl border border-gray-100 p-6 text-center shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/70">
				<p className="text-gray-500 dark:text-gray-400">
					Noch keine Dateien in diesem Raum hochgeladen.
				</p>
			</div>
		);
	}

	return (
		<section className="card rounded-xl border border-gray-100 p-6 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/70">
			<h2 className="mb-4 border-gray-100 border-b pb-2 font-semibold text-gray-800 text-xl dark:border-gray-700 dark:text-gray-100">
				Hochgeladene Dateien
			</h2>
			<div className="space-y-3">
				{dateien.map((datei) => {
					const FileIcon = getFileIcon(datei.typ);
					return (
						<div
							className="flex flex-col items-start justify-between gap-3 rounded-lg border border-gray-100 bg-white p-3 sm:flex-row sm:items-center dark:border-gray-700 dark:bg-gray-800"
							key={datei._id}
						>
							<div className="flex flex-1 items-center gap-3 overflow-hidden">
								<FileIcon className="h-6 w-6 shrink-0 text-blue-500 dark:text-blue-400" />
								<div className="overflow-hidden">
									<p
										className="truncate font-medium text-gray-900 text-sm dark:text-gray-100"
										title={datei.name}
									>
										{datei.name}
									</p>
									<p className="text-gray-500 text-xs dark:text-gray-400">
										{formatBytes(datei.groesse)} - {datei.typ}
									</p>
								</div>
							</div>
							<div className="flex w-full shrink-0 items-center justify-end gap-2 sm:w-auto">
								<Button
									className="border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/30"
									onClick={() => {
										const a = document.createElement("a");
										a.href = `/api/file/download?fileId=${datei._id}`;
										a.download = datei.name;
										document.body.appendChild(a);
										a.click();
										a.remove();
									}}
									size="sm"
									title="Datei herunterladen"
									type="button"
									variant="outline"
								>
									<Download className="mr-1 h-4 w-4 sm:mr-2" />
									<span className="hidden sm:inline">Download</span>
								</Button>
								{canDelete(datei) && (
									<Button
										className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/30"
										onClick={() => handleDelete(datei._id, datei.name)}
										size="sm"
										title="Datei löschen"
										type="button"
										variant="outline"
									>
										<Trash2 className="mr-1 h-4 w-4 sm:mr-2" />
										<span className="hidden sm:inline">Löschen</span>
									</Button>
								)}
							</div>
						</div>
					);
				})}
			</div>
		</section>
	);
}
